import React from 'react';
import { Badge, Card, Stack } from 'react-bootstrap';
import { BsPencilFill, BsPersonFill } from 'react-icons/bs';
import { FaCircleMinus } from 'react-icons/fa6';
import { Link, useNavigate } from 'react-router-dom';
import Sticker from '../../../../../assets/images/cooking-club-sticker.png';
import { useCartContext } from '../../../../../context';
import { useToast } from '../../../../../context/ToastContext';
import { useDeleteOrderMutation } from '../../../../../services/endpoints/orders/orders';
import { Order } from '../../../../../types/orders.type';
import { getErrorMessage } from '../../../../../utils/utils';
import { CourseCount, OrderCourseDates, OrderCourseTimes } from './OrderCourseDates';
import { getSessionBadgeClass } from '../../../../../utils/sessionColors';

interface OrderCardProps {
  order: Order;
  index?: number;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, index }) => {
  const { course, registrations } = order;
  const navigate = useNavigate();
  const [deleteOrder] = useDeleteOrderMutation();
  const { addToast } = useToast();
  const cartContext = useCartContext();
  const children = registrations.map((r, idx) => (
    <span
      key={idx}
      className="label mt-0"
    >
      {r.child.full_name}
    </span>
  ));

  const removeCourse = async () => {
    try {
      await deleteOrder(order.id).unwrap();
      addToast('Successfully removed course from cart', 'success');
      cartContext.removeCourses([order.course_id]);
    } catch (error) {
      addToast(getErrorMessage(error), 'error');
    }
  };

  const editCourse = () => {
    navigate(`/sessions/${course.slug ?? course.id}?action=edit`);
  };

  return (
      <Card className={`course-card shadow`}>
        <Card.Body>
          <Stack direction="horizontal">
            <Stack className="flex-grow-1" style={{ minWidth: 0 }}>
              <Badge
                  className={`text-uppercase align-self-start mb-2 ${getSessionBadgeClass(index ?? 0)}`}
              >
                Session {(index ?? 0) + 1}
              </Badge>
              <Stack
                  direction="horizontal"
                  gap={2}
              >
                <div>
                  <img
                      src={Sticker}
                      height={50}
                      width={50}
                      alt=""
                  />
                </div>
                <Stack className="me-3">
                  <h3 className={`my-0`}>
                    <Link
                        to={`/sessions/${course.slug ?? course.id}`}
                        className="text-info fw-bolder"
                        style={{ textDecoration: 'none' }}
                    >
                      {course.title}
                    </Link>
                  </h3>
                  <OrderCourseDates order={order} />
                  <OrderCourseTimes order={order} />
                </Stack>
              </Stack>

              <Stack>
                <CourseCount count={order.lesson_count ?? course.lessons?.length ?? 0} />
                <Stack
                    direction="horizontal"
                    gap={1}
                    className="text-info"
                >
                  <BsPersonFill />
                  <span className="text-info">Students</span>
                  <Badge bg="light">x{children.length}</Badge>
                </Stack>
                <Stack style={{ marginLeft: '20px' }}>{children}</Stack>
              </Stack>
            </Stack>
            <Stack
                direction="horizontal"
                gap={3}
                className="ms-auto d-md-none"
                style={{ alignSelf: 'flex-start', flexShrink: 0 }}
            >
              <span
                  className="clickable text-primary"
                  onClick={() => editCourse()}
                  aria-label="Edit"
              >
                <BsPencilFill size={18} />
              </span>
              <span
                  className="clickable"
                  style={{ color: 'var(--bs-sfc-pink)' }}
                  onClick={() => removeCourse()}
                  aria-label="Remove"
              >
                <FaCircleMinus size={20} />
              </span>
            </Stack>
            <Stack
                direction="vertical"
                gap={3}
                className="ms-auto d-none d-md-flex"
                style={{ alignSelf: 'flex-start', flexShrink: 0 }}
            >
            <span
                className="text-uppercase text-primary fw-bolder clickable"
                onClick={() => editCourse()}
            >
              <BsPencilFill size={14} className="mb-1" />
              &nbsp;Edit
            </span>
              <span
                  className="text-uppercase fw-bolder clickable"
                  style={{ color: 'var(--bs-sfc-pink)' }}
                  onClick={() => removeCourse()}
              >
                <FaCircleMinus size={16} className="mb-1" />
                &nbsp;Remove
              </span>
            </Stack>
          </Stack>
        </Card.Body>
      </Card>
  );
};
